import { z } from "zod";

import { Timeframe } from "../enum/timeframe.enum";

export const AnalyticsMetricsSchema = z.object({
	totalLeads: z.number(),
	foundLeads: z.number(),
	qualifiedLeads: z.number(),
	prio1Leads: z.number(),
	prio2Leads: z.number(),
	prio3Leads: z.number(),
	assignedLeads: z.number(),
	// SQL conditional-aggregate counts arrive as strings from the driver — coerce at the boundary
	// (web CLAUDE.md §6) so consumers (percentages, bars) get real numbers.
	contactedLeads: z.coerce.number(),
	endedCampaignLeads: z.coerce.number(),
	totalRepliedLeads: z.number(),
	totalPositiveRepliedLeads: z.number(),
	totalCredits: z.number(),
	totalBadTimingRepliedLeads: z.number(),
	totalWrongPersonRepliedLeads: z.number(),
	totalNotInterestedRepliedLeads: z.number(),
	totalNeutralRepliedLeads: z.number(),
	// Leads whose best reply is an out-of-office auto-reply. Counted inside
	// totalRepliedLeads, so "replies excluding OOO" is the difference of the two.
	totalOooRepliedLeads: z.coerce.number().default(0),
	emailsSendUnique: z.number(),
	emailOpened: z.number(),
	emailClicked: z.number(),
	emailReplies: z.number(),
	emailPositiveReplied: z.number(),
	emailUnsubscribed: z.number(),
	linkedinInviteSent: z.number(),
	linkedinInvitesAccepted: z.number(),
	linkedinReplies: z.number(),
	linkedinPositiveReplied: z.number(),
	totalEmailsSent: z.number(),
	totalLinkedinMesssagesSent: z.number(),
	emailBounced: z.number(),
});

export type AnalyticsMetrics = z.infer<typeof AnalyticsMetricsSchema>;

export const KpiBreakdownBucketSchema = z.object({
	key: z.string().nullable(),
	label: z.string(),
	metrics: AnalyticsMetricsSchema.partial(),
});

export type KpiBreakdownBucket = z.infer<typeof KpiBreakdownBucketSchema>;

// A dimension may be a flat list of buckets, or a map of sub-dimensions
// (e.g. `role: { seniority: [...], jobTitle: [...] }`).
const SegmentBucketsSchema = z.union([
	z.array(KpiBreakdownBucketSchema),
	z.record(z.string(), z.array(KpiBreakdownBucketSchema)),
]);

export const AnalyticsSegmentsSchema = z.object({
	role: SegmentBucketsSchema.optional(),
	geo: SegmentBucketsSchema.optional(),
	industry: SegmentBucketsSchema.optional(),
});

export type AnalyticsSegments = z.infer<typeof AnalyticsSegmentsSchema>;

// UI-level dimension used by the breakdown tabs. Distinct from the backend
// `segmentBy` request field because the backend returns role as a nested
// object ({ seniority, jobTitle }) that we expose as two separate tabs.
export const SegmentDimensionSchema = z.enum(["seniority", "jobTitle", "geo", "industry"]);
export type SegmentDimension = z.infer<typeof SegmentDimensionSchema>;

export const SegmentByRequestFieldSchema = z.enum(["role", "geo", "industry"]);
export type SegmentByRequestField = z.infer<typeof SegmentByRequestFieldSchema>;

/**
 * Credit figures live outside `AnalyticsMetricsSchema` because they are nullable
 * ratios, not KPI counts — keeping them out means `keyof AnalyticsMetrics` (used by
 * the KPI row configs) stays a map of plain numbers.
 */
export const AnalyticsCreditsSchema = z.object({
	// `usage` has no campaignId, so a campaign selection cannot narrow spend the way
	// it narrows every other KPI. The API returns both readings and the UI labels them:
	// totalCredits over-counts (whole list), campaignAttributedCredits under-counts
	// (misses list-level spend that carries no lead). Null when no campaign is selected.
	campaignAttributedCredits: z.coerce.number().nullable().default(null),
	creditsPerQualifiedLead: z.coerce.number().nullable().default(null),
	creditsPerReply: z.coerce.number().nullable().default(null),
	creditsPerPositiveReply: z.coerce.number().nullable().default(null),
});

export type AnalyticsCredits = z.infer<typeof AnalyticsCreditsSchema>;

export const AnalyticsSchema = AnalyticsMetricsSchema.extend({
	...AnalyticsCreditsSchema.shape,
	segments: AnalyticsSegmentsSchema.optional(),
});

export type Analytics = z.infer<typeof AnalyticsSchema>;

export const AnalyticsRequestSchema = z.object({
	campaigns: z.array(
		z.object({
			listId: z.number(),
			campaignIds: z.array(z.string()).optional(),
		})
	),
	from: z.date().optional().nullable(),
	to: z.date().optional().nullable(),
	segmentBy: z.array(SegmentByRequestFieldSchema).optional(),
	// Drill-through filters. Shape matches the Contacts filter payload
	// (`IFilterWithValues`) so the backend can reuse the same parser.
	filters: z.record(z.string(), z.unknown()).optional(),
	// Narrows every KPI to leads these users actually sent to (matched on schedule.userId).
	senderUserIds: z.array(z.number()).optional(),
});

export type AnalyticsRequest = z.infer<typeof AnalyticsRequestSchema>;
